// src/pages/PoliticaPrivacidad.jsx
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function formatDate(year, month, day) {
  const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const PoliticaPrivacidad = () => {
  const lastUpdated = formatDate(2025, 12, 15);

  const categories = [
    {
      title: 'Datos de Identificación',
      items: [
        'Nombre completo',
        'Correo electrónico',
        'Empresa u organización',
        'RFC (cuando aplica)',
      ],
      color: 'green',
      icon: '👤',
    },
    {
      title: 'Datos de Contacto',
      items: [
        'Número telefónico',
        'Dirección de entrega',
        'Código postal',
        'Estado y municipio',
      ],
      color: 'blue',
      icon: '📍',
    },
    {
      title: 'Datos del Pedido',
      items: [
        'Número de orden',
        'Productos solicitados',
        'Fechas de entrega',
        'Estado del envío',
      ],
      color: 'purple',
      icon: '📦',
    },
    {
      title: 'Datos Técnicos de Acceso',
      items: [
        'Dirección IP del dispositivo',
        'Marcas de tiempo de acceso',
        'Cookies de sesión y seguridad',
        'Registros de eventos del sistema',
      ],
      color: 'orange',
      icon: '💻',
    },
  ];

  const colorStyles = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-100',
      iconBg: 'bg-green-500',
      title: 'text-green-700',
      dot: 'bg-green-400',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      iconBg: 'bg-blue-500',
      title: 'text-blue-700',
      dot: 'bg-blue-400',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-100',
      iconBg: 'bg-purple-500',
      title: 'text-purple-700',
      dot: 'bg-purple-400',
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-100',
      iconBg: 'bg-orange-500',
      title: 'text-orange-700',
      dot: 'bg-orange-400',
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <Header />
      <section
        className="mt-24 pt-32 pb-20 px-4 relative bg-cover bg-center bg-no-repeat bg-packsys-blue bg-blend-multiply"
        style={{
          backgroundImage: 'url(/tracking_politica_privacidad.png)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-800/20 to-blue-500/30"></div>
        {/* Overlay para mejor legibilidad del texto */}
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 animate-fade-in-up drop-shadow-lg">
            Política de <span className="text-packsys-orange">Privacidad</span>
          </h1>
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mt-4">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-blue-600 font-medium">
              Última actualización: {lastUpdated}
            </span>
          </div>
        </div>
      </section>

      <main className="flex-1 pt-12 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Contenido Principal */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 transform transition-all duration-700 hover:shadow-3xl">
            {/* Introducción */}
            <section className="mb-12 animate-slide-up">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 text-xl">🏢</span>
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Compromiso con tu Privacidad
                  </h2>
                  <p className="text-gray-600">
                    En Packsys, valoramos y respetamos tu privacidad. Esta
                    política explica cómo recopilamos, usamos, protegemos y
                    compartimos tu información cuando utilizas nuestro sistema
                    de Tracking de Pedidos.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg">ℹ️</span>
                  </div>
                  <h3 className="text-xl font-bold text-blue-700">
                    Base Legal
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Esta política se elabora en cumplimiento de la{' '}
                  <strong>
                    Ley Federal de Protección de Datos Personales en Posesión de
                    los Particulares (LFPDPPP)
                  </strong>{' '}
                  y su Reglamento. Para cualquier duda sobre el tratamiento de
                  tus datos personales, puedes contactarnos en{' '}
                  <span className="text-blue-600 font-medium">
                    legal@packsys.com
                  </span>
                </p>
              </div>
            </section>

            {/* Información que Recopilamos */}
            <section className="mb-12 animate-slide-up animation-delay-300">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">📋</span>
                </div>
                1. Información que Recopilamos
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {categories.map((category, index) => {
                  const colors = colorStyles[category.color];

                  return (
                    <div
                      key={index}
                      className={`${colors.bg} ${colors.border} rounded-2xl p-6 border hover:shadow-md transition-all duration-300`}
                    >
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className={`w-10 h-10 ${colors.iconBg} rounded-full flex items-center justify-center`}
                        >
                          <span className="text-white text-lg">
                            {category.icon}
                          </span>
                        </div>
                        <h3 className={`font-bold ${colors.title} text-lg`}>
                          {category.title}
                        </h3>
                      </div>

                      {/* Items */}
                      <ul className="space-y-2">
                        {category.items.map((item, itemIndex) => (
                          <li
                            key={itemIndex}
                            className="flex items-center gap-2"
                          >
                            <div
                              className={`w-2 h-2 ${colors.dot} rounded-full`}
                            ></div>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <h3 className="font-bold text-gray-800 mb-3">
                  Finalidades del Tratamiento
                </h3>
                <p className="text-gray-700 mb-4">
                  Utilizamos tu información para las siguientes finalidades:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Proveer el servicio de tracking de pedidos',
                    'Notificar sobre el estado de tus envíos',
                    'Mejorar la experiencia de usuario',
                    'Cumplir con obligaciones legales',
                    'Prevenir fraudes y abusos',
                    'Responder a solicitudes de soporte',
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white rounded-lg p-3"
                    >
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-sm">✓</span>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Cookies y Tecnologías Similares */}
            <section className="mb-12 animate-slide-up animation-delay-600">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">🍪</span>
                </div>
                2. Cookies y Tecnologías Similares
              </h2>

              <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100 mb-6">
                <h3 className="font-bold text-amber-800 text-xl mb-4">
                  ¿Qué son las cookies?
                </h3>
                <p className="text-gray-700 mb-4">
                  Las cookies son pequeños archivos de texto que se almacenan en
                  tu dispositivo cuando visitas nuestro sitio. Nos ayudan a:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {[
                    {
                      type: 'Esenciales',
                      desc: 'Necesarias para el funcionamiento básico',
                      examples: ['Sesión de usuario', 'Seguridad'],
                      required: true,
                    },
                    {
                      type: 'Funcionales',
                      desc: 'Mejoran la experiencia',
                      examples: ['Preferencias', 'Personalización'],
                      required: false,
                    },
                    {
                      type: 'Analíticas',
                      desc: 'Nos ayudan a mejorar',
                      examples: ['Estadísticas', 'Rendimiento'],
                      required: false,
                    },
                  ].map((cookie, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-4 border border-amber-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold text-amber-700">
                          {cookie.type}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            cookie.required
                              ? 'bg-red-100 text-red-600'
                              : 'bg-green-100 text-green-600'
                          }`}
                        >
                          {cookie.required ? 'Requeridas' : 'Opcionales'}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">
                        {cookie.desc}
                      </p>
                      <div className="space-y-1">
                        {cookie.examples.map((example, exIndex) => (
                          <div
                            key={exIndex}
                            className="flex items-center gap-2"
                          >
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                            <span className="text-gray-700 text-xs">
                              {example}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/*
                <div className="bg-white rounded-xl p-4 border border-amber-300">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚙️</span>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">
                        Control de Cookies
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Puedes gestionar o deshabilitar las cookies desde la
                        configuración de tu navegador. Ten en cuenta que
                        deshabilitar cookies esenciales puede afectar el
                        funcionamiento del sistema.
                      </p>
                    </div>
                  </div>
                </div>
                */}
              </div>
            </section>

            {/* Compartir Información */}
            <section className="mb-12 animate-slide-up animation-delay-900">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">🤝</span>
                </div>
                3. Compartir Información con Terceros
              </h2>

              <div className="space-y-6">
                <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                  <h3 className="font-bold text-red-700 text-xl mb-4">
                    ¿Con quién compartimos?
                  </h3>

                  <div className="space-y-4">
                    {[
                      {
                        category: 'No Compartimos tu Información',
                        description:
                          'Todos los datos que se recopilan a través de nuestro sistema de tracking, provienen de nuestra nube con base en las compras realizadas a través de las diferentes plataformas como:',
                        examples: [
                          'Amazon',
                          'Mercado Libre',
                          'Tienda Packsys',
                          'Venta Directa',
                        ],
                        consent: 'IMPORTANTE',
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl p-4 border border-red-200"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-bold text-gray-800">
                            {item.category}
                          </h4>
                          <span
                            className={`text-xs px-3 py-1 rounded-full ${
                              item.consent === 'IMPORTANTE'
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {item.consent}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">
                          {item.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.examples.map((example, exIndex) => (
                            <span
                              key={exIndex}
                              className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded"
                            >
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/*
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-300">
                  <h3 className="font-bold text-gray-800 text-xl mb-4">
                    Transferencias Internacionales
                  </h3>
                  <p className="text-gray-700 mb-4">
                    En algunos casos, tus datos pueden ser transferidos y
                    procesados fuera de México. En dichos casos, garantizamos
                    que:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      'Se aplican cláusulas contractuales estándar',
                      'Los países receptores tienen niveles adecuados de protección',
                      'Cumplimos con el Capítulo III de la LFPDPPP',
                      'Te notificamos sobre transferencias relevantes',
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 text-sm">✓</span>
                        </div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                */}
              </div>
            </section>

            {/* Seguridad de Datos */}
            <section className="mb-12 animate-slide-up animation-delay-1200">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">🛡️</span>
                </div>
                4. Seguridad de tus Datos
              </h2>

              <div className="bg-cyan-50 rounded-2xl p-6 border border-cyan-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {[
                    {
                      title: 'Cifrado Avanzado',
                      desc: 'Datos en tránsito y en reposo protegidos con cifrado TLS/SSL',
                      icon: '🔐',
                    },
                    {
                      title: 'Acceso Restringido',
                      desc: 'Control estricto de acceso basado en roles y responsabilidades',
                      icon: '👁️',
                    },
                    {
                      title: 'Monitoreo Continuo',
                      desc: 'Sistemas de detección y prevención de intrusiones 24/7',
                      icon: '📊',
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-5 text-center border border-cyan-200 hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="text-3xl mb-3">{item.icon}</div>
                      <h4 className="font-bold text-cyan-700 mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl p-5 border border-cyan-300">
                  <h3 className="font-bold text-gray-800 mb-4">
                    Nuestro Compromiso
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Implementamos medidas administrativas, técnicas y físicas
                    para proteger tus datos contra accesos no autorizados,
                    alteración, divulgación o destrucción.
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                    <span>
                      Sistema certificado bajo estándares internacionales de
                      seguridad
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Tus Derechos ARCO */}
            <section className="mb-12 animate-slide-up animation-delay-1500">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">⚖️</span>
                </div>
                5. Tus Derechos ARCO
              </h2>

              <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100">
                <p className="text-gray-700 mb-6">
                  Como titular de datos personales, tienes derecho a ejercer los
                  siguientes derechos:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    {
                      letter: 'A',
                      title: 'Acceso',
                      desc: 'Conocer qué datos tenemos de ti y cómo los utilizamos',
                      icon: '👁️',
                    },
                    {
                      letter: 'R',
                      title: 'Rectificación',
                      desc: 'Corregir datos inexactos o incompletos',
                      icon: '✏️',
                    },
                    {
                      letter: 'C',
                      title: 'Cancelación',
                      desc: 'Eliminar tus datos cuando ya no sean necesarios',
                      icon: '🗑️',
                    },
                    {
                      letter: 'O',
                      title: 'Oposición',
                      desc: 'Oponerte al tratamiento de tus datos',
                      icon: '✋',
                    },
                  ].map((derecho, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-4 text-center border border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-white text-xl font-bold">
                          {derecho.letter}
                        </span>
                      </div>
                      <h4 className="font-bold text-purple-700 mb-2">
                        {derecho.title}
                      </h4>
                      <p className="text-gray-600 text-xs">{derecho.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl p-5 border border-purple-300">
                  <h3 className="font-bold text-gray-800 mb-4">
                    ¿Cómo ejercer tus derechos?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-purple-600 text-sm">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">
                          Envía una solicitud por escrito
                        </p>
                        <p className="text-gray-600 text-sm">
                          Dirigida a nuestro Departamento de Privacidad en{' '}
                          <span className="text-purple-600">
                            legal@packsys.com
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-purple-600 text-sm">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">
                          Incluye información requerida
                        </p>
                        <p className="text-gray-600 text-sm">
                          Nombre completo, identificación oficial, descripción
                          clara del derecho a ejercer
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-purple-600 text-sm">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">
                          Recibe respuesta en tiempo
                        </p>
                        <p className="text-gray-600 text-sm">
                          Te responderemos dentro del plazo establecido por la
                          ley
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Retención de Datos */}
            <section className="mb-12 animate-slide-up animation-delay-1800">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">⏳</span>
                </div>
                6. Retención de Datos
              </h2>

              <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center">
                    <span className="text-indigo-600 text-2xl">📅</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-indigo-700 text-xl mb-2">
                      ¿Por cuánto tiempo guardamos tus datos?
                    </h3>
                    <p className="text-gray-700">
                      Conservamos tu información personal únicamente por el
                      tiempo necesario para cumplir con las finalidades
                      descritas en esta política, salvo que la ley requiera o
                      permita un período de retención más largo.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      category: 'Datos Activos',
                      time: 'Durante la relación comercial',
                      desc: 'Mientras seas usuario activo de Packsys',
                    },
                    {
                      category: 'Datos Históricos',
                      time: 'Hasta 5 años',
                      desc: 'Para cumplir con obligaciones legales y fiscales',
                    },
                    {
                      category: 'Datos Anonimizados',
                      time: 'Indefinidamente',
                      desc: 'Para análisis estadísticos y mejora del servicio',
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-4 border border-indigo-200"
                    >
                      <h4 className="font-bold text-indigo-600 mb-2">
                        {item.category}
                      </h4>
                      <div className="text-2xl font-bold text-gray-800 mb-2">
                        {item.time}
                      </div>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Cambios a la Política */}
            <section className="mb-12 animate-slide-up animation-delay-2100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-gray-600 to-gray-700 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">🔄</span>
                </div>
                7. Cambios a esta Política
              </h2>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-300">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white">i</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 text-xl mb-3">
                      Actualizaciones y Notificaciones
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Podemos actualizar esta Política de Privacidad
                      periódicamente para reflejar cambios en nuestras prácticas
                      o por otros motivos operativos, legales o reglamentarios.
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">1</span>
                        </div>
                        <span className="text-gray-700">
                          Publicaremos la versión actualizada en esta página
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">2</span>
                        </div>
                        <span className="text-gray-700">
                          Actualizaremos la fecha de "Última actualización"
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-blue-700 text-sm">
                        <strong>Recomendación:</strong> Te sugerimos revisar
                        periódicamente esta página para mantenerte informado
                        sobre cómo protegemos tu información.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Contacto */}
            <section className="animate-fade-in animation-delay-2400">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-packsys-orange to-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">📞</span>
                </div>
                8. Contacto y Preguntas
              </h2>

              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-orange-700 text-xl mb-4">
                      Departamento Legal
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <span className="text-orange-600">✉️</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">
                            Correo Electrónico
                          </p>
                          <p className="text-orange-600">legal@packsys.com</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-orange-700 text-xl mb-4">
                      Horario de Atención
                    </h3>
                    <div className="bg-white rounded-xl p-4 border border-orange-300">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-700">
                            Lunes a Viernes:
                          </span>
                          <span className="font-medium text-orange-600">
                            7:00 AM - 6:00 PM
                          </span>
                        </div>

                        <div className="pt-2 mt-2 border-t border-orange-200">
                          <p className="text-gray-600 text-sm">
                            Para solicitudes de derechos ARCO, por favor utiliza
                            el correo electrónico indicado.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <p className="text-gray-700 text-sm">
                        <strong>Autoridad Supervisora:</strong> Instituto
                        Nacional de Transparencia, Acceso a la Información y
                        Protección de Datos Personales (INAI)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Botones de Navegación */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center animate-fade-in animation-delay-2700">
              <p className="text-gray-600 mb-6">
                Al utilizar nuestro sistema de Tracking de Pedidos, confirmas
                que has leído, entendido y aceptas esta Política de Privacidad.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/tracking"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-packsys-orange to-orange-500 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform"
                >
                  <span>← Volver al Tracking</span>
                </a>
                <a
                  href="/terminos-de-servicio"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform"
                >
                  <span>📄 Ver Términos de Servicio</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PoliticaPrivacidad;
